import { Checkbox, Column, Row, useAppPalette, Text } from "@bridgesplit/ui";
import { DispatchType } from "@bridgesplit/react";

type CheckboxRowProps<T> = {
    label: string;
    isSelected: boolean;
    setSelected: DispatchType<Set<T>>;
    caption?: string;
    icon?: JSX.Element;
    value: T;
};

type CheckboxListProps<T> = {
    label: string;
    selected: Set<T>;
    setSelected: DispatchType<Set<T>>;
    rows: Omit<CheckboxRowProps<T>, "isSelected" | "setSelected">[];
};

function CheckboxRow<T>({ label, isSelected, setSelected, caption, icon, value }: CheckboxRowProps<T>) {
    const { hoverBackground } = useAppPalette();

    const toggleSelected = () => {
        setSelected((prev) => {
            const curr = new Set(prev);
            if (curr.has(value)) {
                curr.delete(value);
            } else {
                curr.add(value);
            }
            return curr;
        });
    };

    return (
        <Row
            onClick={toggleSelected}
            sx={{
                py: 0.5,
                cursor: "pointer",
                borderRadius: "4px",
                ":hover": { background: hoverBackground },
                mx: -0.5,
                px: 0.5
            }}
        >
            <Row spacing={1}>
                <Checkbox checked={!!isSelected} />
                {icon && icon}
                <Text variant="body2">{label}</Text>
            </Row>
            <Column>{caption !== undefined && <Text color="caption">{caption}</Text>}</Column>
        </Row>
    );
}

export default function CheckboxList<T>({ label, selected, setSelected, rows }: CheckboxListProps<T>) {
    const selectedRows = rows.filter((r) => selected.has(r.value));
    const allSelected = selectedRows.length === rows.length;

    const toggleAllSelected = () => {
        setSelected((prev) => {
            const curr = new Set(prev);
            if (allSelected) rows.forEach((r) => curr.delete(r.value));
            else rows.forEach((r) => curr.add(r.value));
            return curr;
        });
    };

    return (
        <Column spacing={0.5}>
            <Row spacing={1}>
                <Checkbox
                    indeterminate={!allSelected && !!selectedRows.length}
                    checked={allSelected}
                    onChange={toggleAllSelected}
                />
                <Text variant="body2" color="caption">
                    {label}
                </Text>
            </Row>
            <Column spacing={0}>
                {rows.map((row, i) => (
                    <CheckboxRow
                        key={`checkbox-row-${row.value}-${i}`}
                        value={row.value}
                        label={row.label}
                        icon={row.icon}
                        caption={row.caption}
                        isSelected={selected.has(row.value)}
                        setSelected={setSelected}
                    />
                ))}
            </Column>
        </Column>
    );
}
